import { useState } from "react";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { auth } from "../../firebase";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import { IoEye, IoEyeOff } from "react-icons/io5";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import PageTitle from "components/PageTitle";

const CARD_BG = "#ffffff";
const CARD_BORDER = "1px solid #d8e6f7";

function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = () => {
    if (!currentPassword) return "Please enter your current password.";
    if (newPassword.length < 8) return "New password must be at least 8 characters.";
    if (newPassword !== confirmPassword) return "New passwords do not match.";
    if (currentPassword === newPassword) return "New password must be different from current password.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      setStatus({ type: "error", message: error });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      setStatus({ type: "success", message: "Password updated successfully." });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const code = err?.code || "";
      if (code.includes("wrong-password") || code.includes("invalid-credential")) {
        setStatus({ type: "error", message: "Current password is incorrect." });
      } else if (code.includes("weak-password")) {
        setStatus({ type: "error", message: "Password is too weak. Use at least 8 characters with a mix of letters and numbers." });
      } else if (code.includes("requires-recent-login")) {
        setStatus({ type: "error", message: "Session expired. Please sign out and sign back in, then try again." });
      } else {
        setStatus({ type: "error", message: err.message || "Failed to update password." });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordField = (label, value, setValue, show, setShow) => (
    <TextField
      fullWidth
      type={show ? "text" : "password"}
      label={label}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      size="small"
      sx={{ mb: 2 }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton aria-label={show ? "Hide password" : "Show password"} size="small" onClick={() => setShow(!show)}>
              {show ? <IoEyeOff /> : <IoEye />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageTitle title="Change Password" />
      <VuiBox py={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={8} md={5} lg={4}>
            <Card sx={{ background: CARD_BG, border: CARD_BORDER, p: 3 }}>
              <VuiTypography variant="h6" color="dark" mb={1}>
                Update Your Password
              </VuiTypography>
              <VuiTypography variant="caption" color="text" mb={3} display="block">
                Enter your current password and choose a new one.
              </VuiTypography>

              {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                  {status.message}
                </Alert>
              )}

              <VuiBox component="form" onSubmit={handleSubmit}>
                {passwordField("Current Password", currentPassword, setCurrentPassword, showCurrent, setShowCurrent)}
                {passwordField("New Password", newPassword, setNewPassword, showNew, setShowNew)}
                {passwordField("Confirm New Password", confirmPassword, setConfirmPassword, showConfirm, setShowConfirm)}

                <VuiButton
                  type="submit"
                  color="info"
                  fullWidth
                  disabled={isSubmitting}
                  sx={{ mt: 1 }}
                >
                  {isSubmitting ? "Updating..." : "Update Password"}
                </VuiButton>
              </VuiBox>
            </Card>
          </Grid>
        </Grid>
      </VuiBox>
    </DashboardLayout>
  );
}

export default ChangePassword;

